import React, { useState } from 'react';
import { Modal, Tabs, Tag } from 'antd';
import {
    QuestionCircleOutlined,
    BarChartOutlined,
    CalculatorOutlined,
    DatabaseOutlined,
    SafetyCertificateOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';

export const TAB_GUIDE_CONFIG = {
    overview: {
        title: 'Executive Overview Guide',
        badge: 'Portfolio Level',
        description: 'Cross-functional delivery velocity, high-level KPIs, and departmental delivery benchmarks across the organization.',
        whatIsShown: [
            {
                title: '9 Executive KPI Scorecards',
                detail: 'Top-level cards summarizing Active Projects, Completed Projects, Completed Tasks, Tasks Awaiting Review, Overdue Tasks, On-Time Delivery Rate, Tracked Effort (timer hours), Completed Milestones, and Capacity Workload Risk.',
            },
            {
                title: 'Delivery Velocity & Completion Curve',
                detail: 'Daily completed deliverables (tasks + milestone sign-offs) compared against the benchmark target pace line across the active sprint.',
            },
            {
                title: 'Task Pipeline Distribution Donut',
                detail: 'Proportional breakdown of all active scope tasks across Completed, In Progress, Under Review, Overdue, and Backlog stages.',
            },
            {
                title: 'Departmental Velocity & On-Time Comparison',
                detail: 'Grouped bar chart benchmarking total scope, completed tasks, and overdue items across functional departments (Engineering, Design, Marketing, Operations).',
            },
        ],
        formulas: [
            {
                name: 'On-Time Delivery Rate',
                formula: '((Early Tasks + On-Time Tasks) / Total Evaluated Completed Tasks) × 100%',
                explanation: 'Measures delivery punctuality. Only completed tasks with defined due dates are evaluated; early and on-time deliverables count as positive.',
            },
            {
                name: 'Tracked Effort Hours',
                formula: '∑ (task.actualTimeMs / 3,600,000) for all tasks updated in window',
                explanation: 'Actual effort logged via active stopwatch task timers. Completely independent of shift attendance hours.',
            },
            {
                name: 'Department Completion Rate',
                formula: '(Completed Tasks in Dept / Total Tasks in Dept) × 100%',
                explanation: 'Reflects departmental task throughput for the filtered scope.',
            },
            {
                name: 'Workload Risk Indicator',
                formula: 'High if > 20% team members exceed 105% capacity, otherwise Low',
                explanation: 'Evaluates sprint burn risk and resource overallocation.',
            },
        ],
        dataSources: [
            {
                collection: 'projects',
                fields: 'status, department, isClientProject, projectType, isDeleted',
                notes: 'Strictly excludes system_project_type = "TEAM_MEETINGS" from general project metrics.',
            },
            {
                collection: 'projecttasks',
                fields: 'status, dueDate, completedAt, actualTimeMs, priority, deliveryOutcome',
                notes: 'Excludes draft tasks, deleted tasks, and milestone wrapper items.',
            },
            {
                collection: 'attendances',
                fields: 'date, shiftHours, productiveHours, idleHours',
                notes: 'Summarizes organizational productive hours vs scheduled shifts for the selected period.',
            },
        ],
        businessRules: [
            'Current-State vs Event-Window: Active projects reflects the current snapshot; completed projects/tasks reflects events strictly inside the selected date filter.',
            'System Isolation: Internal meeting minutes (PRJ-9246) are strictly excluded from general delivery velocity.',
            'Double-Counting Prevention: Task stopwatch timers and attendance shifts are measured as distinct dimensions.',
        ],
    },

    daily_activity: {
        title: 'Daily Activity & Time Utilization Guide',
        badge: 'Attendance & Utilization',
        description: 'Granular shift, productive, contextual idle, and overtime tracking across all employees and work modes.',
        whatIsShown: [
            {
                title: 'Top Utilization Scorecards',
                detail: 'Productive Time, Contextual Idle Time, Task Overtime (separated from Attendance OT), and Employee Punctuality Rate.',
            },
            {
                title: 'Daily Shift vs Productive vs Idle Composed Trend',
                detail: 'Stacked daily bars of productive, contextual idle, and break hours, with scheduled shift hours as a continuous trend line.',
            },
            {
                title: 'Time Composition Donut',
                detail: 'Proportional split of tracked shift hours into Productive (Blue), Contextual Idle (Amber), and Break (Gray).',
            },
            {
                title: 'Idle Time by Reason Donut',
                detail: 'Categorizes self-reported or system reasons when employees entered idle states (e.g. Waiting for Review, Client Blocker, Meeting).',
            },
            {
                title: 'Attendance Status & Work Mode Donuts',
                detail: 'Distribution of Present, Late, Absent, Half-Day records and Office vs WFH vs Hybrid workplace modes.',
            },
            {
                title: 'Per-Employee Breakdown & Overtime Comparison',
                detail: 'Horizontal bars showing productive vs idle hours per team member, plus separate bars for Attendance Overtime vs Task Extra Hours.',
            },
        ],
        formulas: [
            {
                name: 'Productive Ratio',
                formula: '(Total Productive Minutes / Total Shift Minutes) × 100%',
                explanation: 'Measures what percentage of clock-in shift time was spent in active productive states.',
            },
            {
                name: 'Punctuality Rate',
                formula: '((Total Attendance Records − Late Clock-ins) / Total Records) × 100%',
                explanation: 'Calculates arrival punctuality based on official office shift start thresholds.',
            },
            {
                name: 'Early Clock-out Shortfall',
                formula: '∑ (earlyClockOut.shortfallMinutes) / 60',
                explanation: 'Total hours lost when employees depart prior to scheduled shift end.',
            },
            {
                name: 'Task Overtime vs Attendance Overtime',
                formula: 'Tracked independently as two distinct metrics',
                explanation: 'Task Overtime = hours logged on approved overtime tasks. Attendance Overtime = total shift time exceeding standard workday hours.',
            },
        ],
        dataSources: [
            {
                collection: 'attendances',
                fields: 'date, createdAt, shiftHours, productiveHours, breakHours, idleHours, idleSessions, overtTime, earlyClockOut, workMode, status',
                notes: 'Indexes by date: { $gte, $lte } with fallback to createdAt to guarantee 100% legacy record coverage.',
            },
            {
                collection: 'attendances.idleSessions',
                fields: 'reason, duration, durationMs, startTime, endTime',
                notes: 'Logs user-selected context whenever the desktop agent or manual timer enters idle state.',
            },
        ],
        businessRules: [
            'Strict Idle-Time Rule (PRD §6): Contextual Idle Time is informative and logged for blocker analysis. It is NEVER deducted from Productive Hours or used to penalize employees.',
            'Overtime Independence: Attendance Overtime and Task Overtime are never summed or merged.',
            'Work Mode Transparency: Office, WFH, and Hybrid records are tagged automatically from geofence/IP metadata.',
        ],
    },

    projects: {
        title: 'Project Portfolio Performance Guide',
        badge: 'Portfolio Health',
        description: 'Comprehensive deliverable tracking, schedule variance deviation, and health scorecards across client and internal projects.',
        whatIsShown: [
            {
                title: 'Portfolio Health KPI Tiles',
                detail: 'Count of projects categorized as On Track (Green), At Risk (Amber), or Delayed (Red).',
            },
            {
                title: 'Schedule Variance Horizon (Days)',
                detail: 'Diverging Recharts Bar Chart centered at zero showing exact days Ahead (+Green) versus Lagging (-Red) for each active project.',
            },
            {
                title: 'Project Type Split Donut',
                detail: 'Proportion of Client Revenue projects versus Internal Tooling / In-House projects.',
            },
            {
                title: 'Active Portfolio Deliverable Cards',
                detail: 'Interactive project cards showing progress percentage, PM name, department, milestone tallies, and task counts. Clicking any card opens its detailed project workspace.',
            },
        ],
        formulas: [
            {
                name: 'Schedule Variance (Days)',
                formula: 'Target Baseline Due Date − Latest Projected Deliverable Completion Date',
                explanation: 'Positive values (+Days) indicate delivery ahead of schedule. Negative values (-Days) indicate projected milestone slippage.',
            },
            {
                name: 'Project Health Status',
                formula: 'Delayed if variance < -5 days; At Risk if variance between -1 and -5 days; On Track if variance >= 0',
                explanation: 'Automatically derived from active sprint milestones and overdue critical path tasks.',
            },
            {
                name: 'Project Deliverable Progress %',
                formula: '(Completed Tasks / Total Tasks in Project) × 100%',
                explanation: 'Computed across all active deliverable tasks linked to the project.',
            },
        ],
        dataSources: [
            {
                collection: 'projects',
                fields: 'name, code, projectCode, department, status, isClientProject, projectType, milestones, members, projectManager',
                notes: 'Excludes soft-deleted projects (isDeleted: true) and meeting minutes.',
            },
            {
                collection: 'projecttasks',
                fields: 'project, status, dueDate, completedAt, itemType',
                notes: 'Used to compute task completion progress and identify overdue milestone blockers.',
            },
        ],
        businessRules: [
            'Client vs In-House: Automatically tagged via isClientProject flag or projectType designation.',
            'System Isolation: Meeting minutes system project (PRJ-9246) is excluded from portfolio deliverable health.',
        ],
    },

    tasks_milestones: {
        title: 'Tasks & Milestones Pipeline Guide',
        badge: 'Delivery Pipeline',
        description: 'Deep dive into deadline delivery timing, priority severity, and stage-gate deliverable completion checkpoints.',
        whatIsShown: [
            {
                title: 'Delivery Timing KPI Tiles',
                detail: 'On-Time Rate %, Completed Early count (>24h ahead), Overdue Tasks count, and Completed Milestones tally.',
            },
            {
                title: 'Delivery Timing Distribution Donut',
                detail: 'Visual breakdown of tasks into Completed Early (Blue), Completed On-Time (Green), Completed Late (Amber), and Overdue (Red) with center On-Time Rate %.',
            },
            {
                title: 'Priority Density Donut',
                detail: 'Proportion of active pipeline tasks categorized by Critical (Red), High (Amber), Medium (Blue), and Low (Slate) severity.',
            },
            {
                title: 'Task Pipeline Status Distribution',
                detail: 'Current inventory of tasks across Backlog, In Progress, Under Review, and Completed stages.',
            },
            {
                title: 'Milestone Stage-Gate Deliverable Cards',
                detail: 'Checkpoints displaying deliverable completion gauges, target deadlines, owner, and stage verification tags.',
            },
        ],
        formulas: [
            {
                name: 'Delivery Outcome Classification',
                formula: 'Early if completed <= dueDate - 24h; On-Time if completed <= dueDate; Late if completed > dueDate; Overdue if uncompleted & now > dueDate',
                explanation: 'Categorizes each deliverable based on delivery date versus target deadline.',
            },
            {
                name: 'On-Time Delivery Rate',
                formula: '((Early + On-Time) / (Early + On-Time + Late)) × 100%',
                explanation: 'Standard executive delivery benchmark across all completed tasks.',
            },
            {
                name: 'Milestone Deliverable Progress %',
                formula: '(Completed Tasks in Milestone / Total Tasks in Milestone) × 100%',
                explanation: 'Measures progress through stage-gate deliverables.',
            },
        ],
        dataSources: [
            {
                collection: 'projecttasks',
                fields: 'title, status, priority, dueDate, completedAt, milestoneId, project, assignee',
                notes: 'Primary source of operational task delivery timing.',
            },
            {
                collection: 'projects.milestones (embedded subdocuments) & ProjectTask (itemType: milestone)',
                fields: 'name, code, dueDate, status, progress, deliverablesSummary',
                notes: 'Both embedded milestone subdocuments and standalone milestone tasks are merged.',
            },
        ],
        businessRules: [
            'Separation of Timing vs Effort: Delivery punctuality (calendar due date) is strictly separated from effort accuracy (timer hours vs estimates). A task can be on-time regardless of effort variance.',
            'Overdue tasks are flagged immediately for intervention and escalation.',
        ],
    },

    team_workload: {
        title: 'Team Workload & Capacity Guide',
        badge: 'Resource Capacity',
        description: 'Analyzes individual effort commitments, 40-hour capacity baselines, and sprint allocation balance to prevent burnout.',
        whatIsShown: [
            {
                title: 'Workload Threshold Tiles',
                detail: 'Members categorized into Optimal (80–100%), Available (<80%), and Overallocated (>105%).',
            },
            {
                title: 'Workload Risk Distribution Donut',
                detail: 'Visual breakdown of team members across capacity bands with center average team load percentage.',
            },
            {
                title: '40-Hour Capacity Baseline vs Assigned Hours Chart',
                detail: 'Recharts Composed Bar Chart with an explicit 40h reference threshold line. Bars are colored Red (>42h), Blue (32–42h), or Green (<32h).',
            },
            {
                title: 'Departmental Capacity & Average Utilization',
                detail: 'Compares total committed hours and average utilization percentage across departments.',
            },
            {
                title: 'Individual Team Member Scorecards',
                detail: 'Searchable member cards showing assigned hours, 40h capacity, active tasks, and overdue sprint tasks.',
            },
        ],
        formulas: [
            {
                name: 'Standard Capacity Baseline',
                formula: '40 hours / week',
                explanation: 'Standard organizational weekly capacity threshold applied to all full-time members.',
            },
            {
                name: 'Assigned Workload Hours',
                formula: '∑ (Math.max(0, estimatedTimeMs − actualTimeMs) / 3,600,000) for active tasks',
                explanation: 'Remaining effort commitment across In Progress, Backlog, and Under Review tasks.',
            },
            {
                name: 'Capacity Utilization %',
                formula: '(Assigned Workload Hours / 40h) × 100%',
                explanation: 'Overallocated: >105%, Optimal: 80%–105%, Available: <80%.',
            },
        ],
        dataSources: [
            {
                collection: 'admins',
                fields: 'name, surname, email, department, role, photo, status',
                notes: 'Filters out inactive or soft-deleted staff members.',
            },
            {
                collection: 'projecttasks',
                fields: 'assignee, status: { In Progress, Backlog, Under Review }, estimatedTimeMs, actualTimeMs, dueDate',
                notes: 'Only active uncompleted tasks contribute to remaining assigned workload.',
            },
        ],
        businessRules: [
            'Burnout Prevention: Overallocated members (>105%) receive warning flags to prompt resource rebalancing.',
            'Standardized 40h Baseline: Avoids arbitrary capacity targets; provides a uniform benchmark across all functional teams.',
        ],
    },

    meetings: {
        title: 'Team Meeting Minutes (PRJ-9246) Guide',
        badge: 'System Isolation',
        description: 'Dedicated analytics and agenda milestone tracking for company meeting minutes without polluting project delivery metrics.',
        whatIsShown: [
            {
                title: 'PRJ-9246 Project System Banner',
                detail: 'Highlights the dedicated meeting repository and its isolated milestone architecture.',
            },
            {
                title: 'Meeting Volume & Action Item KPIs',
                detail: 'Total Meeting Hours, Total Logged Sessions, and Action Items Generated from meeting minutes.',
            },
            {
                title: 'Meeting Duration by Category Donut',
                detail: 'Distribution of meeting time across Daily Standups, Client Reviews, Sprint Planning, Architecture, and Retrospectives.',
            },
            {
                title: 'Daily Meeting Density & Attendance Chart',
                detail: 'Composed chart tracking daily meeting hours alongside attendee headcount.',
            },
            {
                title: 'Meeting Milestone Checkpoints',
                detail: 'Meeting minutes milestones with stage agendas, discussion notes, and pending action items.',
            },
        ],
        formulas: [
            {
                name: 'Meeting Duration (Hours)',
                formula: '∑ (meetingTask.actualTimeMs / 3,600,000) or session duration minutes / 60',
                explanation: 'Calculated from stopwatch timer sessions logged under PRJ-9246.',
            },
            {
                name: 'Category Time Share %',
                formula: '(Category Minutes / Total Meeting Minutes) × 100%',
                explanation: 'Proportion of internal meeting time invested in each meeting discipline.',
            },
        ],
        dataSources: [
            {
                collection: 'projects',
                fields: 'system_project_type: "TEAM_MEETINGS" OR projectCode: "PRJ-9246"',
                notes: 'Strictly queries meeting minutes projects only.',
            },
            {
                collection: 'projects.milestones & projecttasks',
                fields: 'title, name, duration, actualTimeMs, status, milestoneId, actionItems',
                notes: 'Milestones map to meeting categories and recurring agenda items.',
            },
        ],
        businessRules: [
            'Strict System Isolation (PRD §5): Meetings under PRJ-9246 NEVER count toward regular client project delivery velocity or general project overdue counts.',
            'Action Item Tracking: Meeting decisions generate linked action items for accountability.',
        ],
    },

    forecasts: {
        title: 'Predictive Delivery & Confidence Cones Guide',
        badge: 'Predictive AI',
        description: 'Monte Carlo velocity projections, P50/P80 delivery confidence cones, and critical path bottleneck diagnostics.',
        whatIsShown: [
            {
                title: 'Confidence Scorecards',
                detail: 'Projects categorized into High Confidence (>90%), Moderate Variance (70–90%), and Low / At Risk (<70%).',
            },
            {
                title: 'Model Confidence Distribution Donut',
                detail: 'Donut chart displaying the proportion of projects across confidence tiers with center average portfolio confidence.',
            },
            {
                title: 'Predictive Delivery Cone Area Chart (P50 vs P80)',
                detail: 'Multi-series Recharts Area Chart displaying P80 Conservative Variance, P50 Expected Variance, and Optimistic Velocity slippage envelopes in days beyond target due date.',
            },
            {
                title: 'Current Completion Progress vs Risk Score Chart',
                detail: 'Composed chart comparing actual completion progress % (Bars) against critical path risk score (Line).',
            },
            {
                title: 'Project-by-Project Delivery Risk & Bottleneck Cards',
                detail: 'Diagnostic cards showing confidence grade, projected variance days, active critical path bottlenecks, and AI recommendations.',
            },
        ],
        formulas: [
            {
                name: 'P50 Expected Slippage (Days)',
                formula: 'Median projected slip = overdueTasksCount × 3 days (calibrated by team velocity)',
                explanation: '50% probability delivery date factoring current sprint throughput.',
            },
            {
                name: 'P80 Conservative Slippage (Days)',
                formula: 'P50 × 1.6 (factoring dependency depth and review queue latency)',
                explanation: '80% high-confidence delivery buffer recommended for external client commitments.',
            },
            {
                name: 'Optimistic Velocity (Days)',
                formula: 'P50 × 0.5',
                explanation: 'Fastest possible delivery date assuming zero blockers and immediate client approvals.',
            },
            {
                name: 'Critical Path Risk Score (0–100)',
                formula: 'Weighted combination of overdue tasks, dependency chain depth, and review queue wait states',
                explanation: 'Low: 0–30, Moderate: 31–60, High / At Risk: 61–100.',
            },
        ],
        dataSources: [
            {
                collection: 'projects',
                fields: 'name, code, dueDate, status, department',
                notes: 'Target project deadlines and scope boundaries.',
            },
            {
                collection: 'projecttasks',
                fields: 'dueDate, status, dependencies, estimatedTimeMs, actualTimeMs',
                notes: 'Used to model dependency lag, overdue volume, and sprint completion velocity.',
            },
        ],
        businessRules: [
            'Non-Linear Escalation: Bottlenecks on the critical path escalate P80 variance exponentially rather than additively.',
            'Grounded AI Guidance: Bottleneck recommendations are derived strictly from measured overdue tasks and review latency.',
        ],
    },
};

/**
 * TabHelpButton
 * Renders an accessible, stylish '?' button with a tooltip.
 */
export function TabHelpButton({ tabKey, className = '' }) {
    const [open, setOpen] = useState(false);
    const config = TAB_GUIDE_CONFIG[tabKey];

    if (!config) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="View metric definitions, formulas, and data sources"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 border cursor-pointer shadow-2xs 
                    bg-white dark:bg-black border-slate-200/90 dark:border-neutral-800 text-slate-700 dark:text-slate-200 
                    hover:border-blue-500 hover:text-blue-600 dark:hover:text-[#00AAF1] hover:shadow-xs ${className}`}
            >
                <QuestionCircleOutlined className="text-blue-500 text-sm" />
                <span className="hidden sm:inline">Guide &amp; Formulas</span>
            </button>

            <TabHelpGuideModal
                open={open}
                onClose={() => setOpen(false)}
                config={config}
            />
        </>
    );
}

/**
 * TabHelpGuideModal
 * Displays detailed explanation of what is shown, formulas, data sources, and business rules.
 */
export function TabHelpGuideModal({ open, onClose, config }) {
    if (!config) return null;

    const tabItems = [
        {
            key: 'shown',
            label: (
                <span className="flex items-center gap-1.5 font-bold">
                    <BarChartOutlined /> What is Shown
                </span>
            ),
            children: (
                <div className="space-y-3.5 py-2">
                    {config.whatIsShown.map((item, idx) => (
                        <div
                            key={idx}
                            className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-950 border border-slate-200/70 dark:border-neutral-800 space-y-1"
                        >
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                {item.title}
                            </h4>
                            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed pl-4">
                                {item.detail}
                            </p>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            key: 'formulas',
            label: (
                <span className="flex items-center gap-1.5 font-bold">
                    <CalculatorOutlined /> Formulas &amp; Metrics
                </span>
            ),
            children: (
                <div className="space-y-3.5 py-2">
                    {config.formulas.map((item, idx) => (
                        <div
                            key={idx}
                            className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-950 border border-slate-200/70 dark:border-neutral-800 space-y-2"
                        >
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                                    {item.name}
                                </h4>
                            </div>
                            <div className="p-2.5 rounded-lg bg-white dark:bg-black border border-slate-200/60 dark:border-neutral-800 font-mono text-xs text-blue-600 dark:text-[#00AAF1] font-semibold break-all">
                                {item.formula}
                            </div>
                            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                {item.explanation}
                            </p>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            key: 'datasources',
            label: (
                <span className="flex items-center gap-1.5 font-bold">
                    <DatabaseOutlined /> Data Sources &amp; Keys
                </span>
            ),
            children: (
                <div className="space-y-3.5 py-2">
                    {config.dataSources.map((item, idx) => (
                        <div
                            key={idx}
                            className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-950 border border-slate-200/70 dark:border-neutral-800 space-y-2"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    Collection:
                                </span>
                                <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-blue-100 dark:bg-neutral-900 text-blue-700 dark:text-blue-400">
                                    {item.collection}
                                </span>
                            </div>
                            <div>
                                <span className="text-[11px] font-bold text-slate-400 block mb-1">Queried Fields:</span>
                                <div className="font-mono text-xs text-slate-700 dark:text-slate-300 p-2 rounded-lg bg-white dark:bg-black border border-slate-200/60 dark:border-neutral-800 break-all">
                                    {item.fields}
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                                ℹ {item.notes}
                            </p>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            key: 'rules',
            label: (
                <span className="flex items-center gap-1.5 font-bold">
                    <SafetyCertificateOutlined /> Rules &amp; Compliance
                </span>
            ),
            children: (
                <div className="space-y-2.5 py-2">
                    {config.businessRules.map((rule, idx) => (
                        <div
                            key={idx}
                            className="p-3 rounded-xl bg-blue-50/50 dark:bg-neutral-950 border border-blue-100 dark:border-neutral-800 flex items-start gap-2.5 text-xs text-slate-700 dark:text-slate-300"
                        >
                            <InfoCircleOutlined className="text-blue-500 mt-0.5 shrink-0" />
                            <span className="leading-relaxed">{rule}</span>
                        </div>
                    ))}
                </div>
            ),
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={720}
            centered
            className="tab-help-modal"
            styles={{
                content: {
                    background: 'var(--modal-bg, #ffffff)',
                    padding: 24,
                    borderRadius: 20,
                    border: '1px solid var(--modal-border, rgba(226, 232, 240, 0.8))',
                },
            }}
        >
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 dark:border-neutral-800 pb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white font-headline">
                                {config.title}
                            </h3>
                            <Tag color="blue">{config.badge}</Tag>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            {config.description}
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs
                    defaultActiveKey="shown"
                    items={tabItems}
                    className="help-guide-tabs"
                />

                <div className="pt-2 flex justify-end border-t border-slate-100 dark:border-neutral-800">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-900 text-white dark:bg-white dark:text-black hover:opacity-90 transition cursor-pointer"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </Modal>
    );
}
